import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import type { BoundaryFrame, PathPoints, Vec3 } from './markerLineTypes';
import { screenPointToLabelMapPixel } from './cprPixelBrushUtils';
import { worldPointToVoxelIJK } from './cprVoxelUtils';

export const cprScreenPointToWorld = ({
  screenPoint,
  labelMap,
  frame,
  baseCorners,
  offset,
  labelData,
}: {
  screenPoint: PathPoints;
  labelMap: {
    width: number;
    height: number;
    spacingX: number;
    spacingY: number;
    data: Uint8Array;
  } | null;
  frame: BoundaryFrame | null;
  baseCorners: PathPoints[] | null;
  offset: PathPoints;
  labelData: any;
}): Vec3 | null => {
  const local = screenPointToLabelMapPixel({
    screenPoint,
    labelMap,
    baseCorners,
    offset,
  });
  if (!labelMap || !frame || !local) return null;

  if (
    local.x < 0 ||
    local.y < 0 ||
    local.x >= labelMap.width ||
    local.y >= labelMap.height
  ) {
    return null;
  }

  const row = Math.max(0, Math.min(frame.rows.length - 1, Math.floor(local.y)));
  const rowFrame = frame.rows[row];
  if (!rowFrame) return null;

  const xMM = (local.x / Math.max(1, frame.width) - 0.5) * frame.widthMM;
  const world: Vec3 = [
    rowFrame.center[0] + xMM * rowFrame.direction[0],
    rowFrame.center[1] + xMM * rowFrame.direction[1],
    rowFrame.center[2] + xMM * rowFrame.direction[2],
  ];

  const worldToIndex = labelData?.getWorldToIndex?.() as ArrayLike<number> | undefined;
  const dims = labelData?.getDimensions?.() as [number, number, number] | undefined;
  if (worldToIndex && dims) {
    const voxel = worldPointToVoxelIJK(
      world,
      255,
      worldToIndex,
      dims,
      undefined,
      false,
    );
    if (!voxel) return null;
  }

  return world;
};

export const focusLinkedViewsFromCpr = (world: Vec3) => {
  const focusWorld = [world[0], world[1], world[2]] as [number, number, number];
  scheduleRender('Surface3D', {
    type: 'focus',
    world: focusWorld,
    mode: 'cpr',
  });
  scheduleRender('Axial', {
    type: 'focus',
    world: focusWorld,
    mode: 'cpr',
  });
  scheduleRender('Coronal', {
    type: 'focus',
    world: focusWorld,
    mode: 'cpr',
  });
  scheduleRender('Sagittal', {
    type: 'focus',
    world: focusWorld,
    mode: 'cpr',
  });
};
